// src/pages/settings/PaymentMethods.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';

const GREEN = '#2e9f86';
const TYPES = ['cash', 'bank_transfer', 'mobile_payment', 'credit', 'cheque', 'other'];
const asArray = (d) => Array.isArray(d) ? d : Array.isArray(d?.data) ? d.data : [];

export default function PaymentMethods() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {} (new) | row (edit)

  const { data: list = [], isLoading } = useQuery(['payment-methods'], () =>
    api.get('/api/payment-methods').then((r) => asArray(r.data)));

  const save = useMutation(
    (body) => body._id
      ? api.put(`/api/payment-methods/${body._id}`, body)
      : api.post('/api/payment-methods', body),
    { onSuccess: () => { qc.invalidateQueries(['payment-methods']); setEditing(null); },
      onError: (e) => alert(e?.response?.data?.message || e.message) }
  );
  const del = useMutation(
    (id) => api.delete(`/api/payment-methods/${id}`),
    { onSuccess: () => qc.invalidateQueries(['payment-methods']),
      onError: (e) => alert(e?.response?.data?.message || e.message) }
  );

  return (
    <div className="p-5">
      <Header title={t('settings.paymentMethods', 'Payment Methods')} onAdd={() => setEditing({})} t={t} />
      <Table loading={isLoading} list={list} t={t}
             onEdit={setEditing} onDelete={(r) => window.confirm(t('common.confirmDelete', 'Delete this item?')) && del.mutate(r._id)} />
      {editing && (
        <FormModal row={editing} types={TYPES} t={t} busy={save.isLoading}
                   onClose={() => setEditing(null)} onSave={(b) => save.mutate(b)} />
      )}
    </div>
  );
}

function Header({ title, onAdd, t }) {
  return (
    <div className="flex items-center justify-between mb-4">
      <h1 className="text-2xl font-bold text-gray-800">{title}</h1>
      <button onClick={onAdd} className="px-4 py-2 rounded text-white font-bold text-sm" style={{ backgroundColor: GREEN }}>
        + {t('common.add', 'Add')}
      </button>
    </div>
  );
}

function Table({ loading, list, onEdit, onDelete, t }) {
  if (loading) return <div className="text-gray-400 py-8">…</div>;
  if (!list.length) return <div className="text-gray-400 py-8">{t('common.none', 'Nothing yet')}</div>;
  return (
    <div className="bg-white rounded-xl border overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="text-start px-4 py-2">{t('common.name', 'Name')}</th>
            <th className="text-start px-4 py-2">{t('common.type', 'Type')}</th>
            <th className="text-start px-4 py-2">{t('common.status', 'Status')}</th>
            <th className="px-4 py-2 w-28"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((r) => (
            <tr key={r._id} className="border-t">
              <td className="px-4 py-2 font-semibold">{r.name}{r.is_default && <span className="ms-2 text-xs text-emerald-600">({t('common.default', 'default')})</span>}</td>
              <td className="px-4 py-2 text-gray-600">{r.type}</td>
              <td className="px-4 py-2">{r.status || 'active'}</td>
              <td className="px-4 py-2 text-end whitespace-nowrap">
                <button onClick={() => onEdit(r)} className="text-blue-600 font-semibold me-3">{t('common.edit', 'Edit')}</button>
                {!r.is_default && <button onClick={() => onDelete(r)} className="text-red-600 font-semibold">{t('common.delete', 'Delete')}</button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FormModal({ row, types, onClose, onSave, busy, t }) {
  const [f, setF] = useState({ name: row.name || '', name_ar: row.name_ar || '', type: row.type || 'cash', status: row.status || 'active' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal title={row._id ? t('common.edit', 'Edit') : t('common.add', 'Add')} onClose={onClose}>
      <div className="space-y-3">
        <Field label={t('common.name', 'Name')}><input value={f.name} onChange={set('name')} className="inp" /></Field>
        <Field label={t('common.nameAr', 'Arabic name')}><input value={f.name_ar} onChange={set('name_ar')} className="inp" dir="rtl" /></Field>
        <Field label={t('common.type', 'Type')}>
          <select value={f.type} onChange={set('type')} className="inp">{types.map((x) => <option key={x} value={x}>{x}</option>)}</select>
        </Field>
        <Field label={t('common.status', 'Status')}>
          <select value={f.status} onChange={set('status')} className="inp"><option value="active">active</option><option value="inactive">inactive</option></select>
        </Field>
        <Actions busy={busy} disabled={!f.name} onClose={onClose} onSave={() => onSave({ ...row, ...f })} t={t} />
      </div>
    </Modal>
  );
}

/* shared bits */
export function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-[440px] max-w-[92vw] p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>
        {children}
      </div>
      <style>{`.inp{width:100%;border:1px solid #d1d5db;border-radius:.5rem;padding:.5rem .75rem;margin-top:.25rem}`}</style>
    </div>
  );
}
export function Field({ label, children }) {
  return <div><label className="text-sm text-gray-600">{label}</label>{children}</div>;
}
export function Actions({ busy, disabled, onClose, onSave, t }) {
  return (
    <div className="flex gap-2 pt-2">
      <button onClick={onClose} className="flex-1 py-2 rounded bg-gray-100 hover:bg-gray-200 font-semibold">{t('common.cancel', 'Cancel')}</button>
      <button onClick={onSave} disabled={busy || disabled} className="flex-1 py-2 rounded text-white font-bold disabled:opacity-40" style={{ backgroundColor: GREEN }}>
        {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
      </button>
    </div>
  );
}